<script setup lang="ts">
/**
 * Step 7 — Review summary before the actual bizfile submission.
 */
import { computed } from 'vue';
import { formatSgAddressLine } from '@/features/setupEntity/components/sg/utils/sgLocalAddress';
import type { IncorporationEfilingModel } from '../composables/incorporationEfilingModel';

const props = defineProps<{
  model: IncorporationEfilingModel;
}>();

const info = computed(() => props.model.entityInfo);
const registeredLine = computed(() => formatSgAddressLine(info.value.registeredAddress));

const positionsSummary = computed(() =>
  props.model.positionHolders.map((h) => ({
    name: h.name || '(no name)',
    positions: h.positions.join(', '),
    category: h.category === '2' ? 'Corporate' : 'Individual',
  })),
);

const totalShares = computed(() =>
  props.model.currencies.reduce(
    (acc, c) => acc + c.shareClasses.reduce((a, sc) => a + (Number(sc.shareAllotted) || 0), 0),
    0,
  ),
);
</script>

<template>
  <div class="ef-step">
    <v-alert type="info" variant="tonal" density="compact" class="mb-4">
      Review the details below. On submit, the reviewed data is assembled into the ACRA bizfile
      incorporation payload. The actual Singpass-authenticated filing is completed in the following
      step.
    </v-alert>

    <div class="rv-grid">
      <div class="rv-card">
        <div class="rv-card__h">Entity</div>
        <div class="rv-row"><span>Company</span><b>{{ info.companyName || '—' }}</b></div>
        <div class="rv-row"><span>FYE</span><b>{{ info.finYearEnd || '—' }} ({{ info.finYearPeriod }})</b></div>
        <div class="rv-row"><span>Office hours</span><b>{{ info.operatingHourType }}</b></div>
        <div class="rv-row"><span>Email</span><b>{{ info.email || '—' }}</b></div>
        <div class="rv-row"><span>Registered address</span><b>{{ registeredLine || '—' }}</b></div>
      </div>

      <div class="rv-card">
        <div class="rv-card__h">Counts</div>
        <div class="rv-row"><span>Position holders</span><b>{{ model.positionHolders.length }}</b></div>
        <div class="rv-row"><span>Nominators</span><b>{{ model.nominators.length }}</b></div>
        <div class="rv-row"><span>Controllers</span><b>{{ model.controllers.length }}</b></div>
        <div class="rv-row"><span>Share currencies</span><b>{{ model.currencies.length }}</b></div>
        <div class="rv-row"><span>Allotment lines</span><b>{{ model.allotLines.length }}</b></div>
        <div class="rv-row"><span>Total shares allotted</span><b>{{ totalShares }}</b></div>
        <div class="rv-row"><span>Constitution</span><b>{{ model.constitution.type }}<template v-if="model.constitution.type === 'MODEL'"> / {{ model.constitution.modelType }}</template></b></div>
      </div>
    </div>

    <div class="rv-card mt-3">
      <div class="rv-card__h">Position holders</div>
      <div v-if="!positionsSummary.length" class="rv-empty">None.</div>
      <table v-else class="rv-table">
        <thead>
          <tr><th>Name</th><th>Type</th><th>Positions</th></tr>
        </thead>
        <tbody>
          <tr v-for="(p, i) in positionsSummary" :key="i">
            <td>{{ p.name }}</td>
            <td>{{ p.category }}</td>
            <td>{{ p.positions }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.rv-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
}
.rv-card {
  border: 1px solid #e6e9f0;
  border-radius: 10px;
  padding: 12px 14px;
}
.rv-card__h {
  font-size: 13px;
  font-weight: 800;
  color: #1a2336;
  margin-bottom: 8px;
}
.rv-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
  padding: 3px 0;
  border-bottom: 1px dashed #eef1f6;
}
.rv-row span {
  color: #6b7a94;
}
.rv-row b {
  color: #1a2336;
  text-align: right;
}
.rv-empty {
  font-size: 12.5px;
  color: #8a95a8;
}
.rv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}
.rv-table th,
.rv-table td {
  text-align: left;
  padding: 5px 6px;
  border-bottom: 1px solid #eef1f6;
}
.rv-table th {
  color: #6b7a94;
  font-weight: 700;
}
</style>
